import { baseService } from '../base';

export interface SalesAnalytics {
  totalSales: number;
  totalBookings: number;
  totalServices: number;
  averageBookingValue: number;
  growth: number;
}

export interface SalesReport {
  id: string;
  date: string;
  client: string;
  serviceType: string;
  amount: number;
  bookingReference: string;
  status: string;
}

export interface SalesReportsResponse {
  items: SalesReport[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface RevenueByService {
  serviceType: string;
  revenue: number;
}

export interface TopClient {
  clientName: string;
  revenue: number;
}

export interface MonthlyTrend {
  month: number;
  monthName: string;
  revenue: number;
}

export interface SalesFilters {
  startDate?: string;
  endDate?: string;
  clientId?: string;
  serviceType?: string;
  page?: number;
  limit?: number;
}

export const salesApi = {
  // Get sales analytics data
  async getSalesAnalytics(filters: SalesFilters = {}): Promise<SalesAnalytics> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.clientId) params.append('clientId', filters.clientId);

    const response = await baseService.get(`/sales/analytics?${params.toString()}`);
    return response.data;
  },

  // Get detailed sales reports
  async getSalesReports(filters: SalesFilters = {}): Promise<SalesReportsResponse> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.clientId) params.append('clientId', filters.clientId);
    if (filters.serviceType) params.append('serviceType', filters.serviceType);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await baseService.get(`/sales/reports?${params.toString()}`);
    return response.data;
  },

  // Get revenue breakdown by service type
  async getRevenueByService(filters: SalesFilters = {}): Promise<RevenueByService[]> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const response = await baseService.get(`/sales/revenue-by-service?${params.toString()}`);
    return response.data;
  },

  // Get top clients by revenue
  async getTopClients(filters: SalesFilters = {}): Promise<TopClient[]> {
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await baseService.get(`/sales/top-clients?${params.toString()}`);
    return response.data;
  },

  // Get monthly sales trends
  async getMonthlyTrends(year?: number, clientId?: string): Promise<MonthlyTrend[]> {
    const params = new URLSearchParams();
    
    if (year) params.append('year', year.toString());
    if (clientId) params.append('clientId', clientId);

    const response = await baseService.get(`/sales/monthly-trends?${params.toString()}`);
    return response.data;
  },

  // Format currency helper
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  },

  // Format percentage helper
  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  },
}; 